import { CSSProperties, ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Ranking, rankings } from '@tanstack/match-sorter-utils';
import {
  ColumnDef,
  FilterFnOption,
  getCoreRowModel,
  getFilteredRowModel,
  Row,
  TableMeta,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';

import { Button } from 'components/Form/Button/Button';

import { useDataTableContext } from './contexts/DataTableContext';
import { useBulkActions } from './hooks/useBulkActions/useBulkActions';
import { useColumnWidth } from './hooks/useColumnWidth';
import { useDataRenderMode } from './hooks/useDataRenderMode';
import { calculateDataRowPixelHeight } from './hooks/useEmbeddedTableRows/helpers';
import { useGlobalFilter } from './hooks/useGlobalFilter';
import { useRowSelection } from './hooks/useRowSelection';
import { useVirtualizer } from './hooks/useVirtualizer';
import { collectParentPaths } from './utils/pathUtils';
import { HeaderCell } from './cells/HeaderCell';
import { BulkEditDialog } from './components/BulkEditDialog/BulkEditDialog';
import { Search } from './components/Search';
import { SelectionWidget } from './components/SelectionWidget/SelectionWidget';
import { TableBody } from './components/TableBody';
import { TableHeader } from './components/TableHeader';
import { createFuzzyFilter } from './fuzzyFilter';
import { DataRenderModes, DisplayModes } from './Types';

export type DataTableProps<TData, TValue> = {
  /**
   * Component to be displayed in the footer
   */
  footerMenu?: ReactNode;
  /** Provide array of pinned columns ids. Pins to the left. */
  pinnedColumns?: Array<string>;
  /** Enable to show cell borders. */
  showCellBorders?: boolean;
  /**
   * Provide '@tanstack/react-table' configuration
   * @see https://tanstack.com/table/latest/docs/guide/column-defs
   */
  columns: Array<ColumnDef<TData, TValue>>;
  /** Provide table data */
  data: Array<TData> | undefined;
  /** Empty state component */
  emptyState?: ReactNode;
  /**
   * Provide '@tanstack/react-table' meta information
   * @see https://tanstack.com/table/latest/docs/api/core/table#meta
   */
  meta: TableMeta<TData>;
  /** Set render mode for the data. Allows to override default 'auto' setting */
  dataRenderMode?: keyof typeof DataRenderModes;
  /** Change a number of non-visible items rendered in virtualized mode. Needed for debugging */
  overscan?: number;
  // TODO: improve type
  /**
   * Provide a custom filtering function for global search
   * @see https://tanstack.com/table/latest/docs/framework/react/examples/filters-fuzzy
   */
  globalFilterFn?: FilterFnOption<TData>;
  searchPositionStrategy?: 'relative' | 'absolute';
  /** Enable to display search input on the top of the table */
  enableGlobalSearch?: boolean;
  /**/
  globalSearchThreshold?: Ranking;
  /**
   * Provide a function to conditionally enable or disable specific row for selection
   * @example
   * // enable selection conditionally per row
   * <DataTable rowSelectionFn={row => row.original.age > 18} />
   */
  rowSelectionFn?: (row: Row<TData>) => boolean;

  /**
   * Component to be displayed in the header
   */
  headerMenu?: ReactNode;
  /**
   * Enable equal width columns
   */
  equalWidthColumns?: boolean;

  /**
   * Table id
   */
  id?: string;
};

export type DataTableHandle = {
  scrollToIndex: (index: number) => void;
};

const TABLE_HEIGHT = 'calc(100svh - 180px)';
const TABLE_MAX_HEIGHT = '940px'; // 20 rows
const TableInner = <TData, TValue>(
  {
    columns,
    data = [],
    showCellBorders = true,
    emptyState,
    meta,
    pinnedColumns = [],
    headerMenu,
    footerMenu,
    dataRenderMode: dataRenderModeProp = DataRenderModes.auto,
    overscan = 10,
    globalFilterFn: globalFilterFnProp,
    enableGlobalSearch = true,
    searchPositionStrategy = 'relative',
    globalSearchThreshold = rankings.CONTAINS,
    rowSelectionFn,
    equalWidthColumns,
    id,
  }: DataTableProps<TData, TValue>,
  ref: ForwardedRef<DataTableHandle>,
) => {
  const { t } = useTranslation();
  const globalFilterFn = useMemo(() => {
    if (!globalFilterFnProp) {
      return createFuzzyFilter<TData>(globalSearchThreshold);
    }
    return globalFilterFnProp;
  }, [globalFilterFnProp, globalSearchThreshold]);

  const { globalFilter, setGlobalFilter, handleResetFilter } = useGlobalFilter();
  const { rowSelection, enableRowSelection, setRowSelection, displayWidget, clearSelection } = useRowSelection({
    columns,
    rowSelectionFn,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      columnPinning: {
        left: pinnedColumns,
      },
      globalFilter,
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    meta,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn,
    getFilteredRowModel: getFilteredRowModel(),
  });

  const { rows } = table.getRowModel();
  const dataRenderMode = useDataRenderMode({ mode: dataRenderModeProp, rows });

  const parentColumnPaths = useMemo(() => collectParentPaths(columns as Array<ColumnDef<TData, unknown>>), [columns]);
  const enableEmbeddedRowActions = meta?.mode === DisplayModes.create || meta?.mode === DisplayModes.edit;
  const dataRowHeights = useMemo(
    () => rows.map((row) => calculateDataRowPixelHeight(row.original, parentColumnPaths, enableEmbeddedRowActions)),
    [rows, parentColumnPaths, enableEmbeddedRowActions],
  );

  const { parentRef, virtualizer, before, after, virtualRows } = useVirtualizer({
    rows,
    overscan,
    dataRenderMode,
    dataRowHeights,
  });

  useImperativeHandle(
    ref,
    () => ({ scrollToIndex: (index: number) => virtualizer.scrollToIndex(index, { align: 'start' }) }),
    [virtualizer],
  );

  const hasRows = rows.length > 0;
  const scrollStyle: CSSProperties =
    dataRenderMode !== DataRenderModes.plain
      ? { height: TABLE_HEIGHT, maxHeight: TABLE_MAX_HEIGHT }
      : { maxHeight: TABLE_HEIGHT };

  const { setValue } = useDataTableContext();
  const { isBulkEditModalOpen, handleBulkEditModalClose, bulkColumns, handleBulkEdit, handleBulkEditButtonClick } =
    useBulkActions({
      columns,
      rowSelection,
      setTableData: (value) => {
        setValue(undefined, value);
      },
      tableData: data,
    });

  const columnsWidth = useColumnWidth(columns, Boolean(equalWidthColumns));
  const bodyVirtualizationConfig = useMemo(() => ({ before, after, virtualRows }), [after, before, virtualRows]);
  return (
    <>
      <BulkEditDialog
        isOpen={isBulkEditModalOpen}
        closeSelf={handleBulkEditModalClose}
        inputsConfig={bulkColumns}
        onApply={handleBulkEdit}
      />
      <div className="w-full">
        <div
          className={classNames('inset-x-0 top-0', searchPositionStrategy, {
            'mt-3': searchPositionStrategy === 'absolute',
          })}
        >
          {(enableGlobalSearch && data.length > 0) || headerMenu ? (
            <div className="mb-2 flex justify-end gap-2">
              {enableGlobalSearch && data.length > 0 ? (
                <Search filter={globalFilter} onFilterChange={setGlobalFilter} />
              ) : null}
              {headerMenu ? headerMenu : null}
            </div>
          ) : null}
        </div>
        <div className="relative">
          {displayWidget && (
            <SelectionWidget
              rowSelection={rowSelection}
              handleBulkEditButtonClick={handleBulkEditButtonClick}
              clearSelection={clearSelection}
            />
          )}
        </div>
        <div
          ref={parentRef}
          style={scrollStyle}
          id={id}
          className={classNames('relative overflow-auto rounded-md', {
            'border border-gray-200': hasRows,
          })}
        >
          {hasRows ? (
            <table
              className={classNames('w-full border-separate border-spacing-0 text-xs', {
                'table-fixed': equalWidthColumns,
                'table-auto': true,
              })}
            >
              <TableHeader<TData> table={table} showCellBorders={showCellBorders} columnsWidth={columnsWidth} />
              <TableBody<TData, TValue>
                dataRenderMode={dataRenderMode}
                virtualizationConfig={bodyVirtualizationConfig}
                columns={columns}
                showCellBorders={showCellBorders}
                columnsWidth={columnsWidth}
                rows={rows}
                parentColumnPaths={parentColumnPaths}
                enableEmbeddedRowActions={enableEmbeddedRowActions}
              />
            </table>
          ) : (
            <div className="flex w-full flex-col items-center gap-3 rounded-md bg-gray-50 p-4 text-sm text-gray-500">
              <div>{!globalFilter ? emptyState : t('common:dataTable.search.emptyState')}</div>
              {globalFilter && (
                <Button variant="secondary" onClick={handleResetFilter} analyticsId="table_search_clear">
                  {t('common:dataTable.search.clearSearch')}
                </Button>
              )}
            </div>
          )}
        </div>
        {footerMenu ? <div className="mt-3">{footerMenu}</div> : null}
      </div>
    </>
  );
};

const TableWithRef = forwardRef(TableInner) as <TData, TValue>(
  props: DataTableProps<TData, TValue> & { ref?: ForwardedRef<DataTableHandle> },
) => ReactNode;

export const DataTable = Object.assign(TableWithRef, { HeaderCellBuilder: HeaderCell });
